import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    initialStep: { type: Number, default: 0 }
  }

  static targets = [
    "step",
    "stepNavItem",
    "stepLabel",
    "backButton",
    "nextButton",
    "authorizedContactDetails",
    "authorizedContactToggle",
    "authorizedContactItem",
    "authorizedContactTypeToggle",
    "authorizedContactPanel",
    "allowedCategory",
    "allowedChannel",
    "allInformation",
    "contactTypeOtherField",
    "contactPhone",
    "contactPhoneHelp",
    "contactChannelNotice",
    "contactSharingErrors",
    "concussionCount",
    "concussionDetails",
    "concussionDateField",
    "priorConcussion",
    "medications",
    "medicationDetails",
    "submitButton",
    "sportsEmpty",
    "sportsOption",
    "sportsSearch"
  ]

  connect() {
    this.currentStep = this.initialStepValue
    this.showStep(false)
    this.refresh()
  }

  nextStep() {
    if (!this.validateStep(this.currentStep)) return

    if (this.currentStep < this.stepTargets.length - 1) {
      this.currentStep += 1
      this.showStep()
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep -= 1
      this.showStep()
    }
  }

  showStep(shouldScroll = true) {
    if (!this.hasStepTarget) return

    if (this.currentStep < 0 || this.currentStep >= this.stepTargets.length) {
      this.currentStep = 0
    }

    this.stepTargets.forEach((step, index) => {
      const isCurrent = index === this.currentStep

      step.hidden = !isCurrent
      this.stepControls(step).forEach((control) => {
        control.disabled = !isCurrent
      })
    })

    this.stepNavItemTargets.forEach((item, index) => {
      item.classList.toggle("is-current", index === this.currentStep)
      item.classList.toggle("is-complete", index < this.currentStep)
      if (index === this.currentStep) {
        item.setAttribute("aria-current", "step")
      } else {
        item.removeAttribute("aria-current")
      }
    })

    if (this.hasStepLabelTarget) {
      this.stepLabelTarget.textContent = `Step ${this.currentStep + 1} of ${this.stepTargets.length}`
    }

    if (this.hasBackButtonTarget) this.backButtonTarget.hidden = this.currentStep === 0
    if (this.hasNextButtonTarget) this.nextButtonTarget.hidden = this.currentStep === this.stepTargets.length - 1
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.hidden = this.currentStep !== this.stepTargets.length - 1

    this.refresh()

    if (shouldScroll) {
      this.element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  refresh() {
    const contactRequested = this.authorizedContactRequested()

    if (this.hasAuthorizedContactDetailsTarget) {
      this.authorizedContactDetailsTarget.hidden = !contactRequested
      this.authorizedContactDetailsTarget
        .querySelectorAll("[data-guardian-approval-required-when-visible]")
        .forEach((field) => {
          field.required = contactRequested
        })
    }
    this.refreshAuthorizedContactCards()

    this.refreshPhoneRequirement(contactRequested)
    this.refreshHealthFollowups()
    this.refreshConcussionDate()
    this.refreshAllInformationState()
    this.refreshContactSharingErrors()
    this.refreshSubmitButton()
  }

  filterSports() {
    if (!this.hasSportsSearchTarget) return

    const query = this.sportsSearchTarget.value.trim().toLowerCase()
    let visibleCount = 0

    this.sportsOptionTargets.forEach((option) => {
      const matches = option.dataset.sportName.includes(query)
      option.hidden = !matches
      if (matches) visibleCount += 1
    })

    if (this.hasSportsEmptyTarget) {
      this.sportsEmptyTarget.hidden = visibleCount > 0
    }
  }

  toggleAllInformation(event) {
    event.preventDefault()
    event.stopPropagation()

    const fields = this.allowedInformationFields(event.currentTarget)
    const checkedCount = fields.filter((field) => field.checked).length
    const shouldCheck = checkedCount === 0

    fields.forEach((field) => {
      field.checked = shouldCheck
    })

    this.refresh()
  }

  refreshAllInformationState() {
    const allControl = this.hasAllInformationTarget ? this.allInformationTarget : this.element.querySelector("[data-guardian-approval-all-information]")
    if (!allControl || !this.hasAllowedCategoryTarget) return

    const selectable = this.allowedInformationFields(allControl)
    const checkedCount = selectable.filter((field) => field.checked).length

    allControl.checked = selectable.length > 0 && checkedCount === selectable.length
    allControl.indeterminate = checkedCount > 0 && checkedCount < selectable.length
  }

  authorizedContactRequested() {
    if (this.hasAuthorizedContactTypeToggleTarget && this.authorizedContactTypeToggleTargets.some((field) => field.checked)) return true
    if (!this.hasAuthorizedContactToggleTarget) return false

    return this.authorizedContactToggleTargets.some((field) => {
      return field.checked && field.value === "1"
    }) || this.authorizedContactToggleTarget.value === "1"
  }

  refreshPhoneRequirement(contactRequested) {
    if (this.hasAuthorizedContactPanelTarget) {
      this.authorizedContactPanelTargets.forEach((panel) => this.refreshPanelPhoneRequirement(panel))
      return
    }

    if (!this.hasContactPhoneTarget) return

    const phoneRequired = contactRequested && this.phoneOrTextSelected()
    this.contactPhoneTarget.required = phoneRequired

    if (this.hasContactChannelNoticeTarget) {
      this.contactChannelNoticeTarget.hidden = !(phoneRequired && this.contactPhoneTarget.value.trim() === "")
    }
  }

  refreshAuthorizedContactCards() {
    if (!this.hasAuthorizedContactItemTarget) return

    this.authorizedContactItemTargets.forEach((item) => {
      const toggle = item.querySelector("[data-guardian-approval-form-target~='authorizedContactTypeToggle']")
      const panel = item.querySelector("[data-guardian-approval-form-target~='authorizedContactPanel']")
      const otherField = item.querySelector("[data-guardian-approval-form-target~='contactTypeOtherField']")
      const shouldShow = toggle?.checked

      item.classList.toggle("is-open", shouldShow)

      if (panel) this.togglePanel(panel, shouldShow)
      if (otherField) this.togglePanel(otherField, shouldShow && item.dataset.contactType === "other")
    })
  }

  refreshPanelPhoneRequirement(panel) {
    const selectedChannels = Array.from(panel.querySelectorAll("input[name$='[allowed_channels][]']:checked"))
      .map((field) => field.value)
    const phoneRequired = selectedChannels.some((value) => ["phone", "sms"].includes(value))
    const phoneField = panel.querySelector("[data-guardian-approval-form-target~='contactPhone']")
    const phoneHelp = panel.querySelector("[data-guardian-approval-form-target~='contactPhoneHelp']")
    const notice = panel.querySelector("[data-guardian-approval-form-target~='contactChannelNotice']")

    if (phoneField) phoneField.required = phoneRequired
    if (phoneHelp) {
      phoneHelp.textContent = phoneRequired
        ? "Required because phone or text communication is selected."
        : "Required if phone or text communication is selected."
    }
    if (notice && phoneField) {
      notice.hidden = !(phoneRequired && phoneField.value.trim() === "")
    }
  }

  refreshConcussionDate() {
    if (!this.hasConcussionCountTarget || !this.hasConcussionDateFieldTarget) return

    const count = Number.parseInt(this.concussionCountTarget.value, 10)
    const concussionSelected = this.hasPriorConcussionTarget &&
      this.priorConcussionTargets.some((field) => field.checked && field.value === "true")
    const shouldShow = concussionSelected && Number.isFinite(count) && count > 0

    this.concussionDateFieldTarget.hidden = !shouldShow
    this.concussionDateFieldTarget.querySelectorAll("input, select, textarea").forEach((field) => {
      field.disabled = !shouldShow
      if (!shouldShow) field.value = ""
    })
  }

  refreshHealthFollowups() {
    const concussionSelected = this.hasPriorConcussionTarget &&
      this.priorConcussionTargets.some((field) => field.checked && field.value === "true")
    const medicationSelected = this.hasMedicationsTarget &&
      this.medicationsTargets.some((field) => field.checked && field.value === "true")

    if (this.hasConcussionDetailsTarget) this.togglePanel(this.concussionDetailsTarget, concussionSelected)
    if (this.hasMedicationDetailsTarget) this.togglePanel(this.medicationDetailsTarget, medicationSelected)
  }

  refreshSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !this.element.checkValidity() || !this.contactSharingValid()
    this.submitButtonTarget.setAttribute("aria-disabled", this.submitButtonTarget.disabled.toString())
  }

  validateSubmission(event) {
    if (this.hasStepTarget) {
      for (let index = 0; index < this.stepTargets.length; index += 1) {
        if (!this.validateStep(index)) {
          event.preventDefault()
          this.currentStep = index
          this.showStep()
          return
        }
      }
    }

    if (this.contactSharingValid()) {
      this.enableAllStepControls()
      return
    }

    event.preventDefault()
    this.showContactSharingErrors()
  }

  contactSharingValid() {
    if (!this.authorizedContactRequested()) return true

    return this.allowedCategoryTargets.some((field) => field.checked) &&
      this.allowedChannelTargets.some((field) => field.checked)
  }

  showContactSharingErrors() {
    if (!this.hasContactSharingErrorsTarget) return

    this.contactSharingErrorsTarget.hidden = false
    this.contactSharingErrorsTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  refreshContactSharingErrors() {
    if (!this.hasContactSharingErrorsTarget || this.contactSharingErrorsTarget.hidden) return

    this.contactSharingErrorsTarget.hidden = this.contactSharingValid()
  }

  allowedInformationFields(allControl) {
    const group = allControl.closest(".compact-options")
    if (!group) return this.allowedCategoryTargets

    const fields = Array.from(group.querySelectorAll("input[name='guardian_approval[allowed_categories][]'], input[name$='[allowed_categories][]']"))
    return fields.length > 0 ? fields : this.allowedCategoryTargets
  }

  phoneOrTextSelected() {
    if (!this.hasAllowedChannelTarget) return false

    return this.allowedChannelTargets.some((field) => {
      return field.checked && ["phone", "sms"].includes(field.value)
    })
  }

  togglePanel(panel, shouldShow) {
    if (!panel) return

    panel.hidden = !shouldShow
    panel.querySelectorAll("input, select, textarea").forEach((control) => {
      control.disabled = !shouldShow

      if (control.dataset.guardianApprovalRequiredWhenVisible !== undefined) {
        control.required = shouldShow
      }
    })
  }

  validateStep(index) {
    if (!this.hasStepTarget) return true

    const step = this.stepTargets[index]
    if (!step) return true

    this.stepControls(step).forEach((control) => {
      control.disabled = false
    })

    const invalidControl = this.stepControls(step).find((control) => !control.checkValidity())

    if (!invalidControl) {
      if (index !== this.currentStep) {
        this.stepControls(step).forEach((control) => {
          control.disabled = true
        })
      }
      return true
    }

    this.currentStep = index
    this.showStep(false)
    invalidControl.reportValidity()
    invalidControl.focus({ preventScroll: true })
    invalidControl.scrollIntoView({ behavior: "smooth", block: "center" })
    return false
  }

  stepControls(step) {
    return Array.from(step.querySelectorAll("input, select, textarea"))
      .filter((control) => control.type !== "hidden")
  }

  enableAllStepControls() {
    if (!this.hasStepTarget) return

    this.stepTargets.forEach((step) => {
      this.stepControls(step).forEach((control) => {
        control.disabled = false
      })
    })
  }
}
